// src/utils/drawingSite.js
// ---------------------------------------------------------------------------
// A site boundary from a drawing: the traced outline, scaled by one line of
// known length, turned to true north, and placed at a real location.
//
// This is the step that lets a project started from a sketch, photo or PDF be
// saved exactly as a map-traced one is — the map supplies a boundary in latitude
// and longitude, and this produces the same thing from a drawing and three facts
// its owner states: where it is, which way is north, and how long one line is.
//
// Pure functions, no DOM, so they can be checked in Node.
// ---------------------------------------------------------------------------

const R = 6378137;
const rad = (d) => (d * Math.PI) / 180;
const deg = (r) => (r * 180) / Math.PI;

/** Area-weighted centroid of a pixel polygon [[x,y],...]; the vertex mean if degenerate. */
export function polygonCentroid(pts) {
  let a = 0; let cx = 0; let cy = 0;
  for (let i = 0; i < pts.length; i++) {
    const [x1, y1] = pts[i];
    const [x2, y2] = pts[(i + 1) % pts.length];
    const f = x1 * y2 - x2 * y1;
    a += f;
    cx += (x1 + x2) * f;
    cy += (y1 + y2) * f;
  }
  if (Math.abs(a) < 1e-9) {
    return [
      pts.reduce((s, p) => s + p[0], 0) / pts.length,
      pts.reduce((s, p) => s + p[1], 0) / pts.length,
    ];
  }
  return [cx / (3 * a), cy / (3 * a)];
}

/** Shoelace area of a pixel polygon, always positive. */
export function pixelArea(pts) {
  let a = 0;
  for (let i = 0; i < pts.length; i++) {
    const [x1, y1] = pts[i];
    const [x2, y2] = pts[(i + 1) % pts.length];
    a += x1 * y2 - x2 * y1;
  }
  return Math.abs(a) / 2;
}

/** Area on the sphere — the same projection-free formula the map now uses. */
export function sphericalAreaM2(latlngs) {
  let s = 0;
  for (let i = 0; i < latlngs.length; i++) {
    const a = latlngs[i];
    const b = latlngs[(i + 1) % latlngs.length];
    s += (rad(b.lng) - rad(a.lng)) * (2 + Math.sin(rad(a.lat)) + Math.sin(rad(b.lat)));
  }
  return Math.abs((s * R * R) / 2);
}

/** Perimeter by haversine, as the map measures it. */
export function perimeterM(latlngs) {
  let p = 0;
  for (let i = 0; i < latlngs.length; i++) {
    const a = latlngs[i];
    const b = latlngs[(i + 1) % latlngs.length];
    const dLat = rad(b.lat - a.lat);
    const dLng = rad(b.lng - a.lng);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
    p += 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
  }
  return p;
}

/** Metres per pixel from one line of known length; null if it cannot be derived. */
export function scaleFromLine(a, b, lengthM) {
  if (!a || !b) return null;
  const px = Math.hypot(b[0] - a[0], b[1] - a[1]);
  const m = Number(lengthM);
  return px > 0 && m > 0 ? m / px : null;
}

/**
 * The drawing's outline as a latitude/longitude polygon.
 *
 * @param outline   [[x,y],...] in the PARSE's pixel space (image y runs down)
 * @param mpp       metres per pixel, in that same space
 * @param northDeg  where true north points on the drawing, in degrees clockwise
 *                  from the top of the sheet: 0 = up, 90 = to the right
 * @param lat, lng  where the drawing's outline centre sits on the ground
 *
 * The outline is centred on its own centroid, so the location is the middle of
 * the site rather than a corner. A vector the drawing calls "up" has a true
 * bearing of −northDeg; rotating every point by northDeg anticlockwise in
 * east/north coordinates does exactly that.
 */
export function siteFromDrawing({ outline, mpp, northDeg, lat, lng }) {
  if (!Array.isArray(outline) || outline.length < 3 || !(mpp > 0)) return null;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  const [cx, cy] = polygonCentroid(outline);
  const t = rad(Number(northDeg) || 0);
  const cosT = Math.cos(t);
  const sinT = Math.sin(t);
  const cosLat = Math.cos(rad(lat));

  const polygon = outline.map(([x, y]) => {
    const e = (x - cx) * mpp;        // drawing right
    const n = -(y - cy) * mpp;       // drawing up (image y is flipped)
    const E = e * cosT - n * sinT;
    const N = e * sinT + n * cosT;
    return { lat: lat + deg(N / R), lng: lng + deg(E / (R * cosLat)) };
  });

  const xs = outline.map((p) => p[0]);
  const ys = outline.map((p) => p[1]);
  return {
    // The 3D view centres the site on the MEAN of its vertices, not the area
    // centroid — so placing anything from this drawing in 3D needs both.
    vertexMeanPx: [xs.reduce((a, v) => a + v, 0) / xs.length, ys.reduce((a, v) => a + v, 0) / ys.length],
    polygon,
    areaM2: sphericalAreaM2(polygon),
    perimeterM: perimeterM(polygon),
    widthM: (Math.max(...xs) - Math.min(...xs)) * mpp,
    depthM: (Math.max(...ys) - Math.min(...ys)) * mpp,
    centroidPx: [cx, cy],
  };
}

/**
 * Where a drawing project's traced rooms go in the 3D view — exactly.
 *
 * The site was built from this same drawing, so the transform from the drawing's
 * pixels to the ground is already known: its scale, its north and its centre. The
 * generic placement (fitToSite) centres the rooms and turns them to the plot's
 * long axis, which here would ignore the north the owner stated and could put the
 * plan a quarter or half turn out. This returns the placement buildTraceMassing
 * applies directly as `fit`: rotation.y and the world position of the rooms'
 * bounding-box centre.
 *
 * World frame: the 3D view draws a site vertex at (east, −north), measured from
 * the mean of the site's vertices. THREE's rotation.y = a sends local +x to
 * (cos a, −sin a); the drawing's "right" lands at (cos θ, −sin θ) — so a = θ.
 *
 * @param drawing  { mpp, northDeg, centroidPx:[x,y], vertexMeanPx:[x,y] }
 * @param rooms    [{ pts:[[x,y],...], panel }] in parse pixels
 * @param panels   [{ index, box:[x0,y0,...] }] — rooms are registered to their panel
 * @returns { rotY, centre:[x,z], fits, siteLongM, siteShortM } | null
 */
export function placementForDrawing({ drawing, rooms, panels }) {
  const mpp = drawing && drawing.mpp;
  const c = drawing && drawing.centroidPx;
  const m = drawing && drawing.vertexMeanPx;
  if (!(mpp > 0) || !Array.isArray(c) || !Array.isArray(m) || !Array.isArray(rooms) || !rooms.length) return null;

  // buildTraceMassing registers each floor to its own panel frame and takes the
  // FIRST panel with rooms as the base, so its frame is where the drawing sits on
  // the sheet; the bounding box spans every floor in panel-relative pixels.
  const byIndex = new Map((panels || []).map((p) => [p.index, p]));
  let origin = null;
  for (const p of panels || []) {
    if (rooms.some((r) => r.panel === p.index) && Array.isArray(p.box)) { origin = [p.box[0], p.box[1]]; break; }
  }
  if (!origin) return null;
  let x0 = Infinity; let x1 = -Infinity; let y0 = Infinity; let y1 = -Infinity;
  rooms.forEach((r) => {
    const p = byIndex.get(r.panel);
    if (!p || !Array.isArray(p.box)) return;
    (r.pts || []).forEach(([x, y]) => {
      x0 = Math.min(x0, x - p.box[0]); x1 = Math.max(x1, x - p.box[0]);
      y0 = Math.min(y0, y - p.box[1]); y1 = Math.max(y1, y - p.box[1]);
    });
  });
  if (!Number.isFinite(x0)) return null;

  const t = rad(Number(drawing.northDeg) || 0);
  const cosT = Math.cos(t);
  const sinT = Math.sin(t);
  const toEN = (x, y) => {
    const e = (x - c[0]) * mpp;
    const n = -(y - c[1]) * mpp;
    return [e * cosT - n * sinT, e * sinT + n * cosT];
  };
  const [Eb, Nb] = toEN(origin[0] + (x0 + x1) / 2, origin[1] + (y0 + y1) / 2);
  const [Em, Nm] = toEN(m[0], m[1]);   // affine, so the vertex mean maps to the mean point
  return { rotY: t, centre: [Eb - Em, -(Nb - Nm)], fits: true, siteLongM: null, siteShortM: null };
}
